/**
 * <s-other-options> — "Other options" list under the product buy box.
 *
 * Loads Shopify related-product recommendations (routes.product_recommendations_url + ".json")
 * and renders compact rows: packshot, title, price (sale red + yellow "Save" chip + "Was"),
 * and a quick Add button. Add uses Standard Actions (Shopify.actions.updateCart + openCart)
 * when available, otherwise each row's native /cart/add form submits normally.
 *
 * Required markup:
 *   data-url, data-product-id, data-cart-add-url, data-currency
 *   data-label-add, data-label-added, data-label-save / data-label-was (with "__AMOUNT__" placeholder)
 *   [data-options-list] — <ul> to fill; template[data-add-icon] — icon markup for the Add button.
 */
class SOtherOptions extends HTMLElement {
  #ac;

  connectedCallback() {
    if (!this.dataset.url || !this.dataset.productId) return;
    this.#ac = new AbortController();
    this.addEventListener("submit", (event) => this.#onSubmit(event), { signal: this.#ac.signal });
    this.#load();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  async #load() {
    const url = new URL(this.dataset.url, window.location.origin);
    if (!url.pathname.endsWith(".json")) url.pathname += ".json";
    url.searchParams.set("product_id", this.dataset.productId);

    try {
      const response = await fetch(url, { signal: this.#ac.signal, headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(`Other options request failed: ${response.status}`);
      const data = await response.json();
      const all = (data.products || []).filter((p) => String(p.id) !== String(this.dataset.productId));
      const options = all.filter((p) => p.variants?.some((v) => v.available)).slice(0, 4);
      if (!options.length) return;
      const list = this.querySelector("[data-options-list]");
      if (!list) return;
      list.replaceChildren(...options.map((p) => this.#row(p)));
      this.hidden = false;
    } catch (error) {
      if (error.name !== "AbortError") console.warn("[other-options]", error);
    }
  }


  #money(cents) {
    const currency = this.dataset.currency || "AUD";
    const locale = document.documentElement.lang || "en-AU";
    try {
      return new Intl.NumberFormat(locale, { style: "currency", currency, currencyDisplay: "narrowSymbol" }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  #row(product) {
    const variant = product.variants.find((v) => v.available) || product.variants[0];
    const price = variant?.price ?? product.price;
    const compare = variant?.compare_at_price ?? product.compare_at_price;
    const onSale = compare && compare > price;

    const item = this.#el("li", "flex items-center gap-3 py-3");

    const media = this.#el("a", "block h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-border bg-surface p-1");
    media.style.width = "4rem";
    media.style.height = "4rem";
    media.href = product.url;
    media.tabIndex = -1;
    media.setAttribute("aria-hidden", "true");
    const src = product.featured_image || (product.images && product.images[0]);
    if (src) {
      const raw = typeof src === "string" ? src : src.src || src.url || "";
      const base = raw.startsWith("//") ? `https:${raw}` : raw;
      const join = base.includes("?") ? "&" : "?";
      const img = document.createElement("img");
      img.className = "block h-full w-full object-contain";
      img.src = `${base}${join}width=200`;
      img.alt = "";
      img.width = 64;
      img.height = 64;
      img.decoding = "async";
      media.append(img);
    }
    item.append(media);

    const info = this.#el("div", "flex min-w-0 flex-1 flex-col gap-1");
    const title = this.#el("a", "line-clamp-2 text-sm font-bold leading-snug text-on-surface no-underline hover:underline", product.title);
    title.href = product.url;
    info.append(title);

    const priceRow = this.#el("div", "flex flex-wrap items-center gap-2");
    priceRow.append(this.#el("span", `text-base font-extrabold ${onSale ? "text-sale" : "text-on-surface"}`, this.#money(price)));
    if (onSale) {
      const saveText = (this.dataset.labelSave || "Save __AMOUNT__").replace("__AMOUNT__", this.#money(compare - price));
      priceRow.append(this.#el("span", "rounded bg-deal px-1.5 py-0.5 text-xs font-bold text-deal-text", saveText));
    }
    info.append(priceRow);
    if (onSale) {
      const wasText = (this.dataset.labelWas || "Was __AMOUNT__").replace("__AMOUNT__", this.#money(compare));
      info.append(this.#el("span", "text-xs text-on-surface-secondary", wasText));
    }
    item.append(info);

    const form = this.#el("form", "shrink-0");
    form.method = "post";
    form.action = this.dataset.cartAddUrl || "/cart/add";
    const idInput = this.#el("input");
    idInput.type = "hidden";
    idInput.name = "id";
    idInput.value = String(variant.id);
    const qty = this.#el("input");
    qty.type = "hidden";
    qty.name = "quantity";
    qty.value = "1";
    const button = this.#el(
      "button",
      "inline-flex h-11 min-w-24 cursor-pointer items-center justify-center gap-1.5 rounded-button bg-interactive px-4 text-sm font-bold text-interactive-text hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent aria-disabled:cursor-wait aria-disabled:opacity-60",
    );
    button.type = "submit";
    button.setAttribute("aria-label", `${this.dataset.labelAdd || "Add"}: ${product.title}`);
    const icon = this.querySelector("template[data-add-icon]");
    if (icon) button.append(icon.content.cloneNode(true));
    button.append(this.#el("span", "", this.dataset.labelAdd || "Add"));
    form.append(idInput, qty, button);
    item.append(form);

    return item;
  }

  async #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;
    if (!form.getAttribute("action")) return; // native /cart/add fallback

    event.preventDefault();
    const button = form.querySelector("button");
    if (button?.getAttribute("aria-disabled") === "true") return;
    button?.setAttribute("aria-disabled", "true");

    let response;
    try {
      // AJAX Cart API: writes the same session cart the Liquid drawer partials render.
      // The form action is Liquid's routes.cart_add_url; .js returns JSON.
      const variantId = Number(String(form.elements.id.value).split("/").pop());
      response = await fetch(`${form.getAttribute("action")}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        console.warn("[other-options]", data?.description || data?.message);
      } else {
        const label = button?.querySelector("span:last-child");
        if (label) label.textContent = this.dataset.labelAdded || "Added";
        await customElements.get("s-cart")?.refreshMountedCarts?.();
        const dialog = document.getElementById("cart-drawer");
        if (dialog instanceof HTMLDialogElement && !dialog.open) {
          dialog.showModal();
          dialog.dispatchEvent(new CustomEvent("s-cart:opened", { bubbles: true, detail: { dialogId: dialog.id } }));
        }
      }
    } catch (error) {
      console.warn("[other-options]", error);
      if (response?.ok) window.location.reload();
      else form.submit();
    } finally {
      button?.removeAttribute("aria-disabled");
    }
  }
}

if (!customElements.get("s-other-options")) {
  customElements.define("s-other-options", SOtherOptions);
}
